import { useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useBranch } from '@/contexts/BranchContext';
import {
  resolveTimezone,
  buildScheduledAtIso,
  formatInClinicZone,
  clinicDate,
} from '@/utils/clinic-timezone';

/**
 * Resolves the active clinic timezone (selected branch override, else clinic
 * default, else app default) and exposes timezone-aware date helpers bound to
 * it. Use this anywhere you display an API timestamp or build one to send, so
 * the UI shows/sends clinic wall-clock instead of the browser's local zone.
 */
export function useClinicTimezone() {
  const { clinic } = useAuth();
  const { selectedBranch } = useBranch();

  const timezone = useMemo(
    () => resolveTimezone(selectedBranch?.timezone, clinic?.timezone),
    [selectedBranch?.timezone, clinic?.timezone],
  );

  const toScheduledAtIso = useCallback(
    (pickedDate: Date, time: string) =>
      buildScheduledAtIso(pickedDate, time, timezone),
    [timezone],
  );

  const format = useCallback(
    (instant: Date | string, pattern: string) =>
      formatInClinicZone(instant, timezone, pattern),
    [timezone],
  );

  const toClinicDate = useCallback(
    (instant: Date | string) => clinicDate(instant, timezone),
    [timezone],
  );

  return { timezone, toScheduledAtIso, format, toClinicDate };
}
